import { useState } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

// -- Consumer-Friendly Issue Classifications -------------------------------
const CATEGORIES = [
    {
        id: 'integration',
        label: 'API & Integration Issues',
        sub: 'Connection errors, invalid security tokens, or rate-limiting (429) errors.',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
            </svg>
        ),
    },
    {
        id: 'billing',
        label: 'Billing & Account Audits',
        sub: 'Invoices, team subscription upgrades, or manual data volume additions.',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
            </svg>
        ),
    },
    {
        id: 'sensitivity',
        label: 'Custom Sensitivity Controls',
        sub: 'Fine-tuning filtering algorithms or custom filtering rules for your organization.',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" /><path d="M19.07 4.93A10 10 0 0 1 21 12a10 10 0 0 1-1.93 7.07M4.93 4.93A10 10 0 0 0 3 12a10 10 0 0 0 1.93 7.07" />
            </svg>
        ),
    },
    {
        id: 'general',
        label: 'General Account Inquiry',
        sub: 'Account recovery, workspace settings, or basic plan inquiries.',
        icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
    },
]

const STATUS_ITEMS = [
    { label: 'Primary Analysis Engine', status: 'operational' },
    { label: 'Secondary Filter Pipeline', status: 'operational' },
    { label: 'Secure Authentication Node', status: 'operational' },
    { label: 'Stripe Gateway Connector', status: 'operational' },
    { label: 'API Rate Limiting System', status: 'operational' },
]

const MAX_DESC = 2000

// -- Consumer-Optimized Custom Input ----------------------------------------
function TicketInput({ label, id, type = 'text', value, onChange, required, placeholder, maxLength }) {
    return (
        <div className="space-y-1.5">
            <label htmlFor={id} className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id}
                    name={id}
                    type={type}
                    value={value}
                    onChange={onChange}
                    required={required}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    className="w-full bg-white/[0.02] border border-white/[0.08] text-sm font-sans font-light text-white px-4 py-3 rounded-xl outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200 placeholder-slate-600"
                />
                {maxLength && (
                    <span className="absolute right-3.5 bottom-3 text-[10px] font-sans text-slate-600">
                        {value.length}/{maxLength}
                    </span>
                )}
            </div>
        </div>
    )
}

// -- Main Page Code ---------------------------------------------------------
export default function Support() {
    const [category, setCategory] = useState(null)
    const [form, setForm] = useState({ name: '', email: '', subject: '', description: '' })
    const [checklist, setChecklist] = useState({ sha: false, payload: false, whitelist: false })
    const [submitted, setSubmitted] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleChange = (e) =>
        setForm(f => ({ ...f, [e.target.name]: e.target.value }))

    const allChecked = Object.values(checklist).every(Boolean)
    const canSubmit = category && form.name && form.email && form.subject && form.description && allChecked

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!canSubmit) return
        setLoading(true)
        await new Promise(r => setTimeout(r, 1200))
        setLoading(false)
        setSubmitted(true)
    }

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e] text-slate-100 antialiased relative overflow-x-hidden">

            {/* Soft Ambient Background Lights */}
            <div className="absolute top-0 left-1/4 w-[600px] h-[300px] bg-indigo-500/[0.03] blur-[120px] pointer-events-none rounded-full" />
            <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-brand-amber/[0.01] blur-[130px] pointer-events-none rounded-full" />

            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-6 py-16 lg:py-24 relative z-10">

                {/* -- Header Section ---------------------------------------- */}
                <div className="mb-14">
                    <p className="text-[10px] font-sans font-semibold tracking-widest uppercase text-slate-500 mb-3">
                        Help Desk Portal & Support Triaging Desk
                    </p>
                    <h1 className="font-sans font-bold text-3xl sm:text-4xl text-white tracking-tight mb-4">
                        How can we help your operations team?
                    </h1>
                    <p className="text-sm font-sans font-light text-slate-400 max-w-2xl leading-relaxed">
                        Standard developer accounts are reviewed by on-duty engineering teams within 12 business hours. Enterprise tier subscriptions feature priority operational lines with a guaranteed sub-2-hour service response window.
                    </p>
                </div>

                {/* -- Asymmetric Grid Layout ------------------------------------ */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">

                    {/* Left: Interactive Help Form Area */}
                    <div className="lg:col-span-2">
                        {submitted ? (
                            <div className="bg-white/[0.01] border border-white/[0.05] rounded-2xl p-12 text-center shadow-2xl backdrop-blur-sm">
                                <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <h2 className="font-sans font-semibold text-xl text-white mb-2">Support Request Received</h2>
                                <p className="text-sm font-sans font-light text-slate-400 max-w-sm mx-auto mb-6 leading-relaxed">
                                    Your details have been cleanly filed. An internal developer will message you back at <span className="font-medium text-slate-200">{form.email}</span> inside your tier's response window.
                                </p>
                                <button
                                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', description: '' }); setCategory(null); setChecklist({ sha: false, payload: false, whitelist: false }) }}
                                    className="text-xs font-sans font-medium text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-4"
                                >
                                    Open another assistance request
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-8">

                                {/* 01: Issue Classification Selector */}
                                <div className="space-y-4">
                                    <p className="text-[10px] font-sans font-bold tracking-widest uppercase text-slate-500">
                                        01 — Select Issue Classification
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {CATEGORIES.map(cat => (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                onClick={() => setCategory(cat.id)}
                                                className="text-left p-4 rounded-xl transition-all duration-300 flex items-start gap-4 select-none bg-white/[0.01] border"
                                                style={{
                                                    borderColor: category === cat.id ? 'rgba(212,168,67,0.4)' : 'rgba(255,255,255,0.05)',
                                                    backgroundColor: category === cat.id ? 'rgba(212,168,67,0.02)' : 'rgba(255,255,255,0.01)',
                                                }}
                                            >
                                                <div className="flex-shrink-0 mt-0.5 transition-colors duration-200"
                                                    style={{ color: category === cat.id ? '#fbbf24' : '#475569' }}>
                                                    {cat.icon}
                                                </div>
                                                <div>
                                                    <p className="text-xs font-sans font-medium text-white mb-1 leading-snug">
                                                        {cat.label}
                                                    </p>
                                                    <p className="text-[11px] font-sans font-light text-slate-400 leading-normal">
                                                        {cat.sub}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* 02: Profile Contact Context */}
                                <div className="space-y-4">
                                    <p className="text-[10px] font-sans font-bold tracking-widest uppercase text-slate-500">
                                        02 — Contact Profile
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <TicketInput
                                            label="Your Name"
                                            id="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="Alex Thornton"
                                        />
                                        <TicketInput
                                            label="Work Email"
                                            id="email"
                                            type="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                            placeholder="alex@company.com"
                                        />
                                    </div>
                                </div>

                                {/* 03: Subject Header Line */}
                                <div className="space-y-4">
                                    <p className="text-[10px] font-sans font-bold tracking-widest uppercase text-slate-500">
                                        03 — Inquiry Summary
                                    </p>
                                    <TicketInput
                                        label="Subject Line"
                                        id="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        required
                                        placeholder="Briefly state the issue (e.g., Rate limit threshold adjustments for key gmc_...)"
                                        maxLength={200}
                                    />
                                </div>

                                {/* Privacy Notice Callout */}
                                <div className="rounded-xl border border-amber-400/10 bg-amber-400/[0.02] px-5 py-4">
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-0.5 text-amber-400">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                                            </svg>
                                        </div>
                                        <p className="text-xs font-sans font-light text-slate-400 leading-relaxed">
                                            <span className="font-semibold text-amber-400/90">Absolute Privacy Rule:</span> To protect your infrastructure boundary, <strong className="text-slate-300 font-medium">do not copy raw or unmasked message texts into these inputs.</strong> Instead, provide general metadata counters or transaction reference status hashes. Support leads will never ask for message content logs.
                                        </p>
                                    </div>
                                </div>

                                {/* 04: Detailed Description Area */}
                                <div className="space-y-4">
                                    <p className="text-[10px] font-sans font-bold tracking-widest uppercase text-slate-500">
                                        04 — Detailed Breakdown
                                    </p>
                                    <div className="relative">
                                        <textarea
                                            id="description"
                                            name="description"
                                            value={form.description}
                                            onChange={e => setForm(f => ({ ...f, description: e.target.value.slice(0, MAX_DESC) }))}
                                            required
                                            rows={5}
                                            placeholder="Detail the issue, system status parameters, approximate server stamps, or runtime environment traits (production or staging). Do not input sensitive communications data."
                                            className="w-full bg-white/[0.02] border border-white/[0.08] text-sm font-sans font-light text-white px-4 py-3 rounded-xl outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200 placeholder-slate-600 resize-none"
                                        />
                                        <span className="absolute right-3.5 bottom-3 text-[10px] font-sans text-slate-600">
                                            {form.description.length}/{MAX_DESC}
                                        </span>
                                    </div>
                                </div>

                                {/* 05: Verification Action Elements */}
                                <div className="bg-white/[0.01] border border-white/[0.05] rounded-xl p-5 shadow-xl">
                                    <p className="text-[10px] font-sans font-bold tracking-widest uppercase text-slate-500 mb-4">
                                        05 — Troubleshooting Checklist
                                    </p>
                                    <div className="space-y-3.5">
                                        {[
                                            { key: 'sha', label: 'I have confirmed that the API token keys match our active production environment.' },
                                            { key: 'payload', label: 'The diagnostic character strings respect our system size parameters.' },
                                            { key: 'whitelist', label: 'Our incoming web servers are explicitly allowed to send out webhook relays.' },
                                        ].map(item => (
                                            <label key={item.key} className="flex items-start gap-3 cursor-pointer group select-none">
                                                <div
                                                    onClick={() => setChecklist(c => ({ ...c, [item.key]: !c[item.key] }))}
                                                    className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-md flex items-center justify-center transition-all duration-200 cursor-pointer border"
                                                    style={{
                                                        backgroundColor: checklist[item.key] ? '#fbbf24' : 'transparent',
                                                        borderColor: checklist[item.key] ? '#fbbf24' : 'rgba(255,255,255,0.15)',
                                                    }}>
                                                    {checklist[item.key] && (
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                                            stroke="#07090e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span className="text-xs font-sans font-light leading-relaxed transition-colors duration-150"
                                                    style={{ color: checklist[item.key] ? '#f8fafc' : '#94a3b8' }}>
                                                    {item.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Form Submit Actions Row */}
                                <div className="space-y-3">
                                    <button
                                        type="submit"
                                        disabled={!canSubmit || loading}
                                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-sans font-semibold text-sm tracking-wide transition-all duration-200 shadow-xl"
                                        style={{
                                            backgroundColor: canSubmit && !loading ? '#fbbf24' : 'rgba(255,255,255,0.02)',
                                            color: canSubmit && !loading ? '#07090e' : '#475569',
                                            cursor: canSubmit && !loading ? 'pointer' : 'not-allowed',
                                            border: canSubmit && !loading ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-slate-950" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
                                                </svg>
                                                <span>Submitting Request Ticket...</span>
                                            </>
                                        ) : (
                                            'Submit Support Ticket'
                                        )}
                                    </button>
                                    {!canSubmit && !loading && (
                                        <p className="text-center text-xs font-sans font-light text-slate-500">
                                            {!category
                                                ? 'Please pick an issue type above to begin setup.'
                                                : !allChecked
                                                    ? 'Please complete the troubleshooting checklist parameters to unlock.'
                                                    : 'Please verify all fields are completely filled in.'}
                                        </p>
                                    )}
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Right: Sticky Live Telemetry Sidebar Panel */}
                    <div className="lg:sticky lg:top-28 space-y-4">

                        <div className="rounded-2xl bg-white/[0.01] border border-white/[0.05] backdrop-blur-md shadow-2xl overflow-hidden">
                            {/* Header Panel */}
                            <div className="px-5 pt-5 pb-4 border-b border-white/[0.04] flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-sans font-semibold tracking-widest uppercase text-slate-500 mb-0.5">
                                        Live Trackers
                                    </p>
                                    <h3 className="font-sans font-semibold text-sm text-white">
                                        System Telemetry
                                    </h3>
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" style={{ animationDuration: '2.8s' }} />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                    </span>
                                    <span className="text-[10px] font-sans font-bold text-emerald-400 uppercase tracking-wider">
                                        Live
                                    </span>
                                </div>
                            </div>

                            {/* Response Metrics Rows */}
                            <div className="px-5 py-4 border-b border-white/[0.04] grid grid-cols-2 gap-3 bg-white/[0.005]">
                                <div className="bg-white/[0.01] rounded-xl p-3 border border-white/[0.04]">
                                    <p className="text-[9px] font-sans font-medium tracking-wider uppercase text-slate-500 mb-0.5">
                                        Avg Response
                                    </p>
                                    <p className="font-sans font-semibold text-base text-white">4.8 min</p>
                                </div>
                                <div className="bg-white/[0.01] rounded-xl p-3 border border-white/[0.04]">
                                    <p className="text-[9px] font-sans font-medium tracking-wider uppercase text-slate-500 mb-0.5">
                                        SLA Target
                                    </p>
                                    <p className="font-sans font-semibold text-base text-white">99.9%</p>
                                </div>
                            </div>

                            {/* Operational Component Blocks */}
                            <div className="px-5 py-4 space-y-3 border-b border-white/[0.04]">
                                <p className="text-[9px] font-sans font-semibold tracking-widest uppercase text-slate-500">
                                    System Component Health
                                </p>
                                {STATUS_ITEMS.map(item => (
                                    <div key={item.label} className="flex items-center justify-between gap-2 text-xs">
                                        <span className="font-sans font-light text-slate-400">{item.label}</span>
                                        <div className="flex items-center gap-1.5 flex-shrink-0 text-emerald-400/90 font-medium">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_5px_#10b981]" />
                                            <span className="text-[10px] tracking-wide uppercase">Operational</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Service SLA Tier Metrics */}
                            <div className="px-5 py-4 space-y-2.5">
                                <p className="text-[9px] font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                    SLA Response Windows
                                </p>
                                {[
                                    { tier: 'Enterprise / Power Tier', sla: 'Under 2h', highlight: true },
                                    { tier: 'Expert / Professional Tier', sla: 'Under 8h', highlight: false },
                                    { tier: 'Essential / Free Tier', sla: 'Under 12h', highlight: false },
                                ].map(t => (
                                    <div key={t.tier}
                                        className="flex items-center justify-between text-xs py-2 px-3 rounded-lg border"
                                        style={{
                                            backgroundColor: t.highlight ? 'rgba(255,255,255,0.01)' : 'transparent',
                                            borderColor: t.highlight ? 'rgba(255,255,255,0.04)' : 'transparent',
                                        }}>
                                        <span className="font-sans font-light text-slate-400">{t.tier}</span>
                                        <span className="font-sans font-medium text-slate-200">{t.sla}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Quick Reference Nav Links Card */}
                        <div className="rounded-xl bg-white/[0.01] border border-white/[0.05] p-5 space-y-3 shadow-xl">
                            <p className="text-[10px] font-sans font-semibold tracking-widest uppercase text-slate-500 mb-1">
                                Quick References
                            </p>
                            {[
                                { label: 'System API Documentation', href: '/docs' },
                                { label: 'Platform Privacy Protocol', to: '/privacy-policy' },
                                { label: 'Zero Retention Infrastructure', to: '/zero-retention' },
                                { label: 'Enterprise Workspace Deployment', to: '/enterprise' },
                                { label: 'Account Subscription Pricing', to: '/plans' },
                            ].map(({ label, href, to }) =>
                                to ? (
                                    <Link key={label} to={to}
                                        className="flex items-center justify-between group text-xs font-sans font-light py-1 transition-colors duration-150 text-slate-400 hover:text-white">
                                        <span>{label}</span>
                                        <span className="transform group-hover:translate-x-0.5 transition-transform duration-200 text-slate-600 group-hover:text-amber-400">→</span>
                                    </Link>
                                ) : (
                                    <a key={label} href={href}
                                        className="flex items-center justify-between group text-xs font-sans font-light py-1 transition-colors duration-150 text-slate-400 hover:text-white">
                                        <span>{label}</span>
                                        <span className="transform group-hover:translate-x-0.5 transition-transform duration-200 text-slate-600 group-hover:text-amber-400">→</span>
                                    </a>
                                )
                            )}
                        </div>
                    </div>

                </div>
            </div>

            <Footer />
        </div>
    )
}